import { useState, useEffect, useRef, useCallback } from "react";
import { config, useClient, useMicrophoneAndCameraTracks } from "./settings.js";
import AgoraRTC from "agora-rtc-sdk-ng";
import Video from "./Video";
import Controls from "./Controls";

export default function VideoCall(props) {
  const { setInCall, setCall, setOpen } = props;
  const [users, setUsers] = useState([]);
  const [start, setStart] = useState(false);
  const [switchedTrack, setSwitchedTrack] = useState(null);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [localVideoKey, setLocalVideoKey] = useState(0); // forces local renderer remount

  const client = useClient();
  const { ready, tracks } = useMicrophoneAndCameraTracks();

  // ── Refs — never stale inside async callbacks ──────────────────────────────
  const screenTrackRef = useRef(null);
  const [videoTrackScreenShare, setVideoTrackScreenShare] = useState(null);
  const isCameraEnabledRef = useRef(true);
  const cameraWasPublishedRef = useRef(false);
  const tracksRef = useRef(null);
  const isStoppingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { isCameraEnabledRef.current = isCameraEnabled; }, [isCameraEnabled]);

  // ── Single setter that keeps ref + state in sync ───────────────────────────
  const setScreenTrack = (track) => {
    screenTrackRef.current = track;
    setVideoTrackScreenShare(track);
  };

  // ── Stop screen share ──────────────────────────────────────────────────────
  const stopScreenShare = useCallback(async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    const track = screenTrackRef.current;

    try {
      if (track) {
        // 1. Detach native onended to prevent recursive call
        const rawTrack = track.getMediaStreamTrack?.();
        if (rawTrack) rawTrack.onended = null;

        // 2. Unpublish screen track
        try { await client.unpublish([track]); } catch (_) { }

        // 3. Stop + close — also dismisses browser "sharing" banner
        track.stop();
        track.close();

        // 4. Clear UI immediately
        setScreenTrack(null);
      }

      // 5. Give Agora renderer time to release the canvas
      await new Promise(r => setTimeout(r, 400));

      const currentTracks = tracksRef.current;
      const cameraTrack = currentTracks?.[1];

      if (cameraTrack) {
        if (cameraWasPublishedRef.current) {
          // Camera was ON before screen share → restore fully
          try {
            await cameraTrack.setEnabled(true);
            await client.publish([cameraTrack]);
            setIsCameraEnabled(true);
            isCameraEnabledRef.current = true;
            // Bump key so local renderer remounts with fresh DOM node
            setLocalVideoKey(prev => prev + 1);
            console.log("Camera fully restored after screen share");
          } catch (err) {
            console.log("Camera restore failed:", err.message);
          }
        } else {
          // Camera was OFF before screen share → keep it off but in clean state
          // so Controls' muteVideo() can publish it when user turns it on
          try {
            await cameraTrack.setEnabled(false);
            console.log("Camera kept off (was off before screen share)");
          } catch (err) {
            console.log("setEnabled(false) skipped:", err.message);
          }
          // Bump key so Video.js remounts its local player — ready for when
          // camera is turned on again via Controls
          setLocalVideoKey(prev => prev + 1);
        }
      }

      cameraWasPublishedRef.current = false;

    } catch (err) {
      console.error("stopScreenShare error:", err);
    } finally {
      isStoppingRef.current = false;
    }
  }, [client]);

  // ── Start / stop screen share ──────────────────────────────────────────────
  const onScreenChoose = useCallback(async (para) => {
    try {
      if (para === "start") {
        if (screenTrackRef.current) {
          console.warn("Already screen sharing — ignoring");
          return;
        }

        let mediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getDisplayMedia({
            video: { frameRate: 15 },
            audio: false,
          });
        } catch (permErr) {
          console.log("Screen share cancelled/denied:", permErr.message);
          return;
        }

        const mediaStreamTrack = mediaStream.getVideoTracks()[0];
        if (!mediaStreamTrack) return;

        // Browser native "Stop sharing" button → sync with our UI
        mediaStreamTrack.onended = () => {
          console.log("Browser native stop triggered");
          stopScreenShare();
        };

        const customScreenTrack = AgoraRTC.createCustomVideoTrack({
          mediaStreamTrack,
          optimizationMode: "detail",
        });

        const currentTracks = tracksRef.current;
        const cameraTrack = currentTracks?.[1];

        // Snapshot: was camera published (ON) when screen share started?
        const cameraCurrentlyOn = isCameraEnabledRef.current;
        cameraWasPublishedRef.current = cameraCurrentlyOn && !!cameraTrack;

        // ALWAYS unpublish camera before publishing screen share.
        // Even when camera is OFF, the track is still published (just disabled).
        // Publishing screen share while camera is published causes Agora conflicts.
        if (cameraTrack) {
          try {
            await client.unpublish([cameraTrack]);
            console.log("Camera unpublished before screen share (was on:", cameraCurrentlyOn, ")");
          } catch (err) {
            console.log("Camera unpublish skipped:", err.message);
          }
        }

        await client.publish([customScreenTrack]);
        setScreenTrack(customScreenTrack);

      } else {
        await stopScreenShare();
      }
    } catch (err) {
      console.error("onScreenChoose error:", err);
      setScreenTrack(null);
      isStoppingRef.current = false;
    }
  }, [client, stopScreenShare]);

  // ── Camera toggle callback (keeps ref in sync with Controls state) ─────────
  const handleCameraToggle = useCallback((enabled) => {
    setIsCameraEnabled(enabled);
    isCameraEnabledRef.current = enabled;
  }, []);

  // ── Switch camera ──────────────────────────────────────────────────────────
  function switchCamera() {
    if (!navigator.mediaDevices?.getUserMedia) return;
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const videoDevices = devices.filter(d => d.kind === "videoinput");
        const currentTracks = tracksRef.current;
        for (const device of videoDevices) {
          if (device.label !== currentTracks?.[1]?._deviceName) {
            navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId } })
              .then(stream => {
                const videoTrack = stream.getVideoTracks()[0];
                const customCamSwiTrack = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: videoTrack });
                setSwitchedTrack(customCamSwiTrack);
              })
              .catch(err => console.log(err.name, err.message));
            break;
          }
        }
      })
      .catch(err => console.log(err.name, err.message));
  }

  // ── Agora room lifecycle ───────────────────────────────────────────────────
  useEffect(() => {
    const init = async (channelName) => {
      client.removeAllListeners();

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "video") {
          setUsers(prev => prev.find(u => u.uid === user.uid) ? prev : [...prev, user]);
        }
        if (mediaType === "audio") {
          user.audioTrack?.play();
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio") user.audioTrack?.stop();
        if (mediaType === "video") {
          setUsers(prev => prev.filter(u => u.uid !== user.uid));
        }
      });

      client.on("user-left", (user) => {
        setUsers(prev => prev.filter(u => u.uid !== user.uid));
      });

      try {
        const userId = JSON.parse(localStorage.getItem("login_data"))?.userId;
        await client.join(config.appId, channelName, props.token, userId);
      } catch (err) {
        console.error("Join error:", err);
        return;
      }

      const currentTracks = tracksRef.current;
      if (currentTracks) await client.publish([currentTracks[0], currentTracks[1]]);
      setStart(true);
    };

    if (ready && tracks) {
      tracksRef.current = tracks;
      init(props.channelName).catch(console.error);
    }

    return () => {
      (async () => {
        try {
          const screenTrack = screenTrackRef.current;
          if (screenTrack) {
            try { await client.unpublish([screenTrack]); } catch (_) { }
            screenTrack.stop();
            screenTrack.close();
            screenTrackRef.current = null;
          }
          const currentTracks = tracksRef.current;
          for (const t of currentTracks || []) {
            if (t) { t.stop(); t.close(); }
          }
          await client.leave();
        } catch (err) {
          console.error("Cleanup error:", err);
        }
      })();
    };
  }, [props.channelName, client, ready, tracks]);

  return (
    <div
      className="d-flex flex-column video_call"
      style={{ height: "80vh", position: "relative", overflowY: "auto" }}
    >
      <div style={{ height: "100%" }}>
        {start && tracks && (
          <Video
            tracks={tracks}
            users={users}
            videoTrackScreenShare={videoTrackScreenShare}
            client={client}
            switchCamera={switchCamera}
            switchedTrack={switchedTrack}
            localVideoKey={localVideoKey}
          />
        )}
      </div>
      <div
        style={{
          height: "10%",
          width: "100%",
          position: "absolute",
          bottom: "3%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {ready && tracks && (
          <Controls
            users={users}
            setOpen={setOpen}
            tracks={tracks}
            setStart={setStart}
            setInCall={setInCall}
            setCall={setCall}
            onScreenChoose={onScreenChoose}
            videoTrackScreenShare={videoTrackScreenShare}
            endCall={props.endCall}
            switchCamera={switchCamera}
            client={client}
            setIsCameraEnabled={handleCameraToggle}
          />
        )}
      </div>
    </div>
  );
}